import { EMPTY_ARRAY, EMPTY_OBJECT } from 'constants/defaults';
import { BUDGET_INTERVAL, BUDGET_SCOPES, BUDGET_TYPES, RATE_TYPES } from 'constants/project';
import { sortSelectedTagLists } from 'factories/projects';
import { intersectionBy, isEqual, xorWith } from 'lodash-es';
import { getTagEntities, getTags } from 'redux/modules/tags.selectors';
import {
  getAccountFeatures,
  getRequiredFieldsFeatureEnabled,
  getStartOfWeek,
} from 'redux/selectors/accounts';
import {
  getDraft,
  getDraftLabels,
  getDraftLabelsLookup,
  getHasReachedProjectLimit,
  getProjectXhrStatus,
} from 'redux/selectors/projects';
import { getUser, getUserPermissions } from 'redux/selectors/user';
import { getNonDeletedUsersWithSearchResults } from 'redux/selectors/users';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type {
  BudgetInterval,
  BudgetRecurrence,
  BudgetScope,
  BudgetType,
  Draft,
  EnrichedDraft,
  EnrichedProjectUserRelation,
  Project,
  ProjectUser,
  ProjectUserRelation,
  RateType,
} from 'types/project';
import type { Tag, TagListOption } from 'types/tag';
import type { LightUser } from 'types/user';
import { mapDefaultTags } from 'utils/project';
import { keyBy, sortBy } from 'utils/typesafe';
import { getTeamEntities, getUserIdsByTeamId } from './teams';

export const getDraftOriginal = (state: IState) => state.projects.draftOriginal;

export const budgetSectionDisplay = (draft: Draft): boolean => {
  // Convert this to boolean to ensure that even undefined budget_type returns false
  return !!(draft.budget_type !== BUDGET_TYPES.NONE);
};

export const hourlyRateSectionDisplay = (draft: Draft): boolean => {
  return !!(draft.rate_type !== RATE_TYPES.NON_BILLABLE);
};

export const getDraftUserIds = createSelector([getDraft], (draft: Draft | null): Array<number> => {
  return draft?.users.map(u => u.user_id || u.user_id) ?? EMPTY_ARRAY;
});

export const getNonDeletedUserIds = createSelector(
  [getNonDeletedUsersWithSearchResults],
  (users: Array<LightUser>): Array<number> => users.map(u => u.id)
);

interface NormalizedUserMap {
  [key: number]: LightUser;
}

export const getNormalizedUsers = createSelector(
  [getNonDeletedUsersWithSearchResults],
  (users: Array<LightUser>): NormalizedUserMap => keyBy(users, 'id')
);

const normalizeUsers = (users: Array<ProjectUserRelation>): Record<number, ProjectUserRelation> =>
  keyBy(users, 'user_id');

const projectUserFactory = (
  user: LightUser,
  draftUser: ProjectUserRelation
): EnrichedProjectUserRelation => {
  return {
    ...draftUser,
    name: user.name,
    avatar: user.avatar,
    internal_hour_rate: user.internal_hour_rate,
  };
};

export const getEnrichedProjectDraftUsers = createSelector(
  [getDraft, getDraftUserIds, getNonDeletedUserIds, getNormalizedUsers],
  (draft, draftUserIds, userIds, users): Array<EnrichedProjectUserRelation> => {
    if (!draft) {
      return EMPTY_ARRAY;
    }
    const draftUsers = normalizeUsers(draft.users);

    const enrichedUsers: Array<EnrichedProjectUserRelation> = draftUserIds
      .filter(id => userIds.includes(id))
      .map(id => projectUserFactory(users[id], draftUsers[id]));
    return sortBy(enrichedUsers, (user: EnrichedProjectUserRelation) => user.name);
  }
);

const hasTagBudget = (budgetScope: BudgetScope) => budgetScope === BUDGET_SCOPES.TAG;

const getBudget = createSelector([getDraft, getDraftLabels], (draft, draftLabels) => {
  if (!draft) {
    return 0;
  }
  if (!hasTagBudget(draft.budget_scope)) {
    return draft.budget;
  }

  return draftLabels.reduce((sum, label) => (label.budget != null ? sum + label.budget : sum), 0);
});

/**
 * - `true` means that the project has changed from non-billable to billable.
 * - `false` means that the project has changed from billable to non-billable.
 * - `null` means that the project has not changed billable state.
 */
export const getProjectBillableChange = createSelector(
  [getDraft, getDraftOriginal],
  (draft, draftOriginal): boolean | null => {
    if (!draft?.id) return null;

    if (draft.rate_type === 'non-billable' && draftOriginal?.rate_type !== 'non-billable') {
      return false;
    } else if (draft.rate_type !== 'non-billable' && draftOriginal?.rate_type === 'non-billable') {
      return true;
    } else {
      return null;
    }
  }
);

export const getHasModifiedProjectRate = createSelector(
  [getDraft, getDraftOriginal],
  (draft, draftOriginal) =>
    !!draft && !!draftOriginal?.id && areProjectRatesDifferent(draft, draftOriginal)
);

const readDefaultTags = (state: IState): Record<number, number> =>
  state.projects.draft?.defaultTags ?? EMPTY_OBJECT;

export const getHasChangedDefaultTags = createSelector(
  [
    readDefaultTags,
    getDraftOriginal,
    (state: IState) => !!state.projects.draft?.default_labels,
    getTagEntities,
  ],
  (defaultTags, draftOriginal, defaultTagsEnabled, tags): [added: boolean, removed: boolean] => {
    // Never trigger a change warning for new projects
    if (!draftOriginal) return [false, false];

    const defaultTagIds = Object.entries(defaultTags)
      .flat()
      .map(id => +id);

    const originalDefaultTagIds = Object.entries(mapDefaultTags(draftOriginal.labels, tags))
      .flat()
      .map(id => +id);

    const added =
      (!!defaultTagIds.length && !draftOriginal.default_label_ids?.length) ||
      defaultTagIds.some(tag => !draftOriginal.default_label_ids?.includes(tag));
    const removed =
      (!!draftOriginal.default_labels && !defaultTagsEnabled) ||
      (!!originalDefaultTagIds.length && !defaultTagIds.length) ||
      !!originalDefaultTagIds.some(tag => !defaultTagIds.includes(tag));

    return [added, removed];
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);

// @todo Update with new state structure
export const getIsProjectSaveDisabled = createSelector(
  [
    getHasReachedProjectLimit,
    getDraft,
    getProjectXhrStatus,
    getProjectBillableChange,
    getHasModifiedProjectRate,
    getHasChangedDefaultTags,
  ],
  (
    hasReachedLimit,
    draft,
    xhrStatus,
    projectBillableChange,
    hasModifiedProjectRate,
    [hasAddedDefaultTags, hasRemovedDefaultTags]
  ): boolean => {
    if (!draft?.name) {
      return true;
    }
    if (
      draft.enable_labels === 'custom' &&
      draft.required_labels &&
      draft.labels.filter(l => !l.default).length === 0
    ) {
      return true;
    }
    const hasValidClientSelected = draft.client && draft.client.id;
    const hasNoUsers = draft.users && draft.users.length === 0;
    const hasChangedBillableWithoutChoice =
      projectBillableChange && draft.update_hour_billable_state == null;
    const hasChangedRateWithoutChoice =
      hasModifiedProjectRate && !projectBillableChange && draft.update_hour_rates == null;

    const hasChangedDefaultTagsWithoutChoice =
      (hasAddedDefaultTags || hasRemovedDefaultTags) && draft.update_hour_default_labels == null;

    return !!(
      xhrStatus.isSaving ||
      xhrStatus.isLoading ||
      !hasValidClientSelected ||
      (hasReachedLimit && !draft.id) ||
      hasNoUsers ||
      hasChangedBillableWithoutChoice ||
      hasChangedRateWithoutChoice ||
      hasChangedDefaultTagsWithoutChoice
    );
  }
);

export const getAllTags = createSelector([getTags], tags => {
  return tags.reduce<Array<Tag>>((memo, tag) => {
    let list = [tag];
    if (tag.children && tag.children.length) {
      list = [tag, ...tag.children];
    }
    memo.push(...list);
    return memo;
  }, []);
});

export const getTagListOptions = createSelector([getTags], (tags): Array<TagListOption> => {
  return tags
    .filter(t => t.active)
    .map(t => ({ name: t.name, value: t.id, hasChildren: !!t.children?.length }));
});

const getSelectedTagLists = createSelector(
  [getDraftLabelsLookup, getTagEntities],
  (lists, entities) => {
    // Sort id's based on the tag list name.
    return sortSelectedTagLists(lists, entities);
  }
);

const getSelectedTagListIds = createSelector([getSelectedTagLists], lists => {
  return lists.filter(list => !!list.active).map(list => list.id);
});

interface Labels {
  header: string;
  submitButton: string;
}

interface HourlyRateOption {
  label: string;
  value: RateType;
}
interface BudgetTypeOption {
  label: string;
  value: BudgetType;
  disabled?: boolean;
}
interface BudgetScopeOption {
  label: string;
  value: BudgetScope;
}
interface BudgetIntervalOption {
  label: string;
  value: BudgetInterval;
}

const rateTypeOptionsFactory = (): {
  [key: string]: HourlyRateOption;
} => {
  return {
    [RATE_TYPES.PROJECT_RATE]: {
      label: 'Same for everyone',
      value: RATE_TYPES.PROJECT_RATE,
    },
    [RATE_TYPES.USER_RATE]: {
      label: 'Individual rates',
      value: RATE_TYPES.USER_RATE,
    },
  };
};

const budgetTypeOptionsFactory = ({
  disableMoneyBudget = false,
}): {
  [key: string]: BudgetTypeOption;
} => {
  return {
    [BUDGET_TYPES.HOUR]: {
      label: 'Time budget',
      value: BUDGET_TYPES.HOUR,
    },
    [BUDGET_TYPES.MONEY]: {
      label: 'Money budget',
      value: BUDGET_TYPES.MONEY,
      disabled: disableMoneyBudget,
    },
  };
};

const budgetScopeOptionsFactory = (): {
  [_key in 'tag' | 'project']: BudgetScopeOption;
} => ({
  [BUDGET_SCOPES.PROJECT]: {
    label: 'fixed project budget',
    value: BUDGET_SCOPES.PROJECT,
  },
  [BUDGET_SCOPES.TAG]: {
    label: 'individual budgets per tag',
    value: BUDGET_SCOPES.TAG,
  },
});

const budgetIntervalOptionsFactory = (): {
  [_key in 'month' | 'week' | 'none']: BudgetIntervalOption;
} => ({
  none: {
    label: 'None',
    value: BUDGET_INTERVAL.NONE,
  },

  [BUDGET_INTERVAL.MONTH]: {
    label: 'Monthly',
    value: BUDGET_INTERVAL.MONTH,
  },

  [BUDGET_INTERVAL.WEEK]: {
    label: 'Weekly',
    value: BUDGET_INTERVAL.WEEK,
  },
});

const recurrence = <T extends { budget_recurrence: BudgetRecurrence }>(
  withBudgetRecurrence: T | null
) => {
  if (withBudgetRecurrence?.budget_recurrence == null) {
    return {
      hasRecurringBudget: false,
      hasMonthlyRecurringBudget: false,
      hasWeeklyRecurringBudget: false,
    };
  }

  return {
    hasRecurringBudget: withBudgetRecurrence.budget_recurrence.recur !== BUDGET_INTERVAL.NONE,
    hasMonthlyRecurringBudget:
      withBudgetRecurrence.budget_recurrence.recur === BUDGET_INTERVAL.MONTH,
    hasWeeklyRecurringBudget: withBudgetRecurrence.budget_recurrence.recur === BUDGET_INTERVAL.WEEK,
  };
};

export const areProjectRatesDifferent = (
  draft: Draft | Project,
  original: Draft | Project
): boolean => {
  if (draft.rate_type === original.rate_type) {
    if (draft.rate_type === 'non-billable') {
      return false;
    } else if (draft.rate_type === 'project') {
      return (draft.hour_rate ?? 0) !== (original.hour_rate ?? 0);
    } else {
      if (draft.users?.length === original.users?.length) {
        const diff = xorWith(
          draft.users,
          original.users,
          (draft, original) =>
            draft.user_id === original.user_id && draft.hour_rate === original.hour_rate
        );
        return !!diff.length;
      }

      // we're only interested in existing users when it comes to figuring out if rates have changed
      const intersectingIds = intersectionBy(
        draft.users ?? EMPTY_ARRAY,
        original.users ?? EMPTY_ARRAY,
        'user_id'
      );

      return intersectingIds.some(user => {
        const draftUser = draft.users?.find(u => u.user_id === user.user_id);
        const originalUser = original.users?.find(u => u.user_id === user.user_id);

        return (draftUser?.hour_rate ?? 0) !== (originalUser?.hour_rate ?? 0);
      });
    }
  } else {
    if (draft.rate_type === 'non-billable' || original.rate_type === 'non-billable') {
      return true;
    }

    let projectRate = 0;
    let users: Array<ProjectUser | ProjectUserRelation> = [];

    if (draft.rate_type === 'project') {
      projectRate = draft.hour_rate;
      users = original.users ?? [];
    } else {
      projectRate = original.hour_rate;
      users = draft.users ?? [];
    }

    return users.some(user => user.hour_rate !== projectRate);
  }
};

const getRelevantSubscriptionFeatures = createSelector(
  [getAccountFeatures, getStartOfWeek],
  (features, startOfWeek) => {
    return {
      isFeatureInvoiceEnabled: !!features.invoices,
      isFeatureBudgetEnabled: !!features.project_budget,
      isFeatureHourlyRateEnabled: !!features.budgets_hourly_rates,
      isFeatureRequiredTagEnabled: !!features.project_required_fields,
      isRecurringBudgetsFeatureEnabled: !!features.recurring_budget,
      isFeatureInternalCostRateEnabled: !!features.internal_costs,
      startOfWeek,
    };
  }
);

export const getProjectEditorTagLists = createSelector(
  [getTagListOptions, getDraftLabelsLookup, getSelectedTagListIds, readDefaultTags],
  (tagListOptions, selectedTagLists, selectedTagListIds, defaultTags) => {
    // Honestly this is sub-optimal but the tags code is so convoluted that I'm not sure how to do
    // it better without a major refactor
    const manualTagListIds: Array<number> = [];
    const defaultTagListIds: Array<number> = [];
    for (const id of selectedTagListIds) {
      if (selectedTagLists[id].default) {
        defaultTagListIds.push(id);
      } else {
        manualTagListIds.push(id);
      }
    }

    return {
      tagListOptions,
      selectedTagLists,
      selectedTagListIds,
      manualTagListIds,
      defaultTagListIds,
      defaultTags,
    };
  }
);

export const getProjectEditorUiState = createSelector(
  [
    getDraft,
    getDraftOriginal,
    getIsProjectSaveDisabled,
    getProjectEditorTagLists,
    getAllTags,
    getRequiredFieldsFeatureEnabled,
    getUserPermissions,
    getRelevantSubscriptionFeatures,
    getUser,
    getProjectBillableChange,
  ],
  (
    draft,
    draftOriginal,
    isSaveDisabled,
    tagLists,
    allTags,
    isRequireFieldsFeatureEnabled,
    userPermissions,
    relevantSubscriptionFeatures,
    user,
    billableChange
  ) => {
    const isEditing: boolean = !!draft?.id;

    const hasModifiedProjectRate = getHasModifiedProjectRate.resultFunc(draft, draftOriginal);

    const labels: Labels = {
      header: `${!isEditing ? 'New' : 'Update'} project`,
      submitButton: `${!isEditing ? 'Create' : 'Update'} project`,
    };

    const hideMoneyRelatedSettings = user.hide_hourly_rate;
    const budgetTypeOptions = budgetTypeOptionsFactory({
      disableMoneyBudget: hideMoneyRelatedSettings,
    });
    const rateTypeOptions = rateTypeOptionsFactory();
    const budgetScopeOptions = budgetScopeOptionsFactory();
    const budgetIntervalOptions = budgetIntervalOptionsFactory();

    const selectedRateType: HourlyRateOption | null | undefined =
      rateTypeOptions[draft?.rate_type!];
    const selectedBudgetType: BudgetTypeOption | null | undefined =
      budgetTypeOptions[draft?.budget_type!];
    const selectedBudgetScope: BudgetScopeOption | null | undefined =
      budgetScopeOptions[draft?.budget_scope!];
    const selectedBudgetInterval: BudgetIntervalOption | null | undefined =
      budgetIntervalOptions[draft?.budget_recurrence?.recur ?? 'none'];

    const showHourlyRateSection =
      !hideMoneyRelatedSettings && !!draft && hourlyRateSectionDisplay(draft);
    const showInternalHourlyRateSection =
      relevantSubscriptionFeatures.isFeatureInternalCostRateEnabled &&
      !user.hide_internal_hourly_rate &&
      !!draft &&
      hourlyRateSectionDisplay(draft);

    const isRecurringBudgetLocked = isEditing && !!recurrence(draftOriginal).hasRecurringBudget;
    const disableBudgetControls =
      isRecurringBudgetLocked ||
      (hideMoneyRelatedSettings && draft?.budget_type === BUDGET_TYPES.MONEY);

    return {
      isSaveDisabled,
      isEditing,
      labels,
      rateTypeOptions: Object.values(rateTypeOptions),
      budgetTypeOptions: Object.values(budgetTypeOptions),
      budgetScopeOptions: Object.values(budgetScopeOptions),
      budgetIntervalOptions: Object.values(budgetIntervalOptions),
      selectedRateType,
      selectedBudgetType,
      selectedBudgetScope,
      selectedBudgetInterval,
      hasMoneyBudget: draft?.budget_type === BUDGET_TYPES.MONEY,
      hasTimeBudget: draft?.budget_type === BUDGET_TYPES.HOUR,
      hasProjectRate: draft?.rate_type === RATE_TYPES.PROJECT_RATE,
      hasIndividualRate: draft?.rate_type === RATE_TYPES.USER_RATE,
      hasTagBudget: !!draft?.budget_scope && hasTagBudget(draft?.budget_scope),
      ...tagLists,
      allTags,
      allActiveTagIds: allTags.filter(tag => tag.active).map(tag => tag.id),
      allTagIds: allTags.map(t => t.id),
      allTagsCount: allTags.length,
      isRequiredNotesFeatureEnabled: isRequireFieldsFeatureEnabled,
      isTagFeatureEnabled: isRequireFieldsFeatureEnabled,
      isTagBudgetsEnabled: false,
      showHourlyRateSection,
      showInternalHourlyRateSection,
      ...recurrence(draft),
      disableBudgetControls,
      canManageTags: userPermissions.canCreate.tags,
      canRemoveUsers: !!draft && draft.users.length > 0,
      ...relevantSubscriptionFeatures,
      showBudgetSection: !!draft && budgetSectionDisplay(draft),
      showAddHourlyRateButton: !showHourlyRateSection && !hideMoneyRelatedSettings,
      hideMoneyRelatedSettings,
      isRecurringBudgetLocked,
      willChangeToNonBillable: billableChange === false,
      willChangeToBillable: billableChange === true,
      hasModifiedProjectRate,
      originalCurrency: draftOriginal?.currency,
      canModifyCurrency: !draft?.locked_hours || user.admin,
      hasModifiedCurrency:
        !!draftOriginal?.currency && draftOriginal?.currency?.id !== draft?.currency?.id,
      hasModifiedRateType: draft?.rate_type !== draftOriginal?.rate_type,
      hasLockedEntries: !!draft?.locked_hours,
    };
  }
);

export type UIState = ReturnType<typeof getProjectEditorUiState>;

export const getEnrichedProjectDraft = createSelector(
  [getDraft, getEnrichedProjectDraftUsers, getBudget],
  (draft, draftUsers, budget): EnrichedDraft | null => {
    if (!draft) {
      return null;
    }
    return {
      ...draft,
      selectedUsers: draftUsers.map(u => u.user_id),
      users: draftUsers,
      budget,
    };
  }
);

export const getUserTeamCountMap = createSelector(
  [getUserIdsByTeamId, getEnrichedProjectDraft, getTeamEntities],
  (teamIdUserIdMap, draft, teamsById) => {
    if (!draft) return EMPTY_OBJECT;
    const { selectedUsers, team_ids } = draft;
    const selectedUserIdsMap = selectedUsers.reduce<Record<number, boolean>>((idsMap, id) => {
      idsMap[id] = true;
      return idsMap;
    }, {});
    const userTeamCountMap: Record<
      number,
      {
        teamsCount: number;
        teamNamesList: Array<string>;
      }
    > = {};
    team_ids.forEach(selectedTeamId => {
      const selectedTeamUsers = teamIdUserIdMap[selectedTeamId] ?? [];
      selectedTeamUsers.forEach(userId => {
        if (selectedUserIdsMap[userId] === true) {
          const countMap = (userTeamCountMap[userId] = userTeamCountMap[userId] ?? {
            teamsCount: 0,
            teamNamesList: [],
          });
          countMap.teamsCount++;
          countMap.teamNamesList.push(teamsById[selectedTeamId].name);
        }
      });
    });
    return userTeamCountMap;
  }
);
